import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, Layers, Sparkles, Building2, Hammer } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";
import { SERVICES_DATA } from "../data";

function getOptimizedImage(url, width = 1200) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function Home() {
  const { lang, t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/v2/content/projects");
        if (res.ok) {
          const data = await res.json();
          setProjects(data.items || []);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !trackRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const totalHeight = rect.height - window.innerHeight;
      if (totalHeight <= 0) return;
      
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / totalHeight));
      
      const parentWidth = trackRef.current.parentElement ? trackRef.current.parentElement.clientWidth : window.innerWidth * 0.62;
      const maxScroll = Math.max(0, trackRef.current.scrollWidth - parentWidth);
      setTranslateX(progress * maxScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    
    const timer = setTimeout(handleScroll, 100);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      clearTimeout(timer);
    };
  }, [projects]);

  const featuredProject = projects[0];
  const scrollingProjects = projects.slice(1, 7);

  // Language-aware paths
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const projectsPath = lang === "da" ? "/projekter" : "/en/projects";

  const homeSeoTitle = lang === "da"
    ? "CPH Collective | Totalentreprise & Tømrer"
    : "CPH Collective | Turnkey & Carpentry";
  const homeSeoDesc = lang === "da"
    ? "Totalentreprise, byggeprojektering og tømrerarbejde under ét ansvarligt tag i København og på Sjælland. Få et uforpligtende tilbud."
    : "Turnkey construction, building engineering, and carpentry under one accountable contractor in Copenhagen. Request a consultation.";

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen">
      {/* SEO Tags */}
      <SEO title={homeSeoTitle} description={homeSeoDesc} />

      {/* Cinematic Hero Banner */}
      <section className="relative h-screen w-full flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <Parallax speed={-0.12} type="background" className="w-full h-full">
            <img
              src={getOptimizedImage("https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitektur-totalentreprise-koebenhavn.jpg", 2000)}
              alt={lang === "da" ? "CPH Collective - Færdiggjort moderne arkitektur og totalentreprise i København" : "CPH Collective - Completed modern architecture and turnkey project in Copenhagen"}
              className="w-full h-full object-cover object-center"
            />
          </Parallax>
          <div className="absolute inset-0 bg-stone-950/50" />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 mt-16 flex justify-start">
          <Parallax speed={0.04} type="text" className="max-w-4xl">
            <div className="animate-fade-in">
              <span className="text-xs md:text-sm uppercase tracking-[0.4em] text-stone-300 font-mono block mb-6 animate-fade-in">
                CPH Collective · {lang === "da" ? "Totalentreprise & Tømrerarbejde" : "Turnkey & Carpentry"}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.15] mb-8 animate-fade-in-up">
                {lang === "da"
                  ? "Totalentreprise med styr på hele byggeprojektet"
                  : "Turnkey Solutions with Complete Project Control"}
              </h1>
              <p className="text-stone-200 text-base md:text-lg lg:text-xl font-light max-w-3xl leading-relaxed mb-10 animate-fade-in-up delay-100">
                {lang === "da"
                  ? "CPH Collective er en bygge og tømrervirksomhed i København, der samler planlægning, koordinering og udførelse i én løsning. Vi hjælper især bygherrer, erhvervskunder og professionelle partnere med totalentreprise, så jeres byggeprojekt får en tydelig retning og et fast kontaktpunkt fra start."
                  : "CPH Collective is a Copenhagen-based construction and carpentry company providing unified planning, coordination, and execution in a single solution. We assist developers, commercial clients, and professional partners with turnkey construction, providing clear direction and a dedicated point of contact from day one."}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-6 animate-fade-in-up delay-200">
                <Link
                  to={contactPath}
                  className="bg-white text-stone-900 hover:bg-stone-100 transition-all duration-500 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold font-mono text-center"
                >
                  {t("cta.requestQuote")}
                </Link>
                <Link
                  to={contactPath}
                  className="border border-white/40 text-white hover:bg-white hover:text-stone-900 hover:border-white transition-all duration-500 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold font-mono text-center"
                >
                  {t("cta.contactConsultation")}
                </Link>
              </div>
            </div>
          </Parallax>
        </div>

        {/* Bottom Scroll Indicator */}
        <div className="absolute bottom-8 left-6 md:left-12 lg:left-16 z-10 hidden md:flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
          <span>{t("scroll.explore")}</span>
          <div className="w-12 h-[1px] bg-stone-200 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-full w-1/2 bg-stone-500 animate-scroll-line" />
          </div>
        </div>
      </section>

      {/* Intro Section - Editorial Architectural Layout (SEO & Firm Profile) */}
      <section className="py-20 md:py-28 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          {/* Section Header */}
          <div className="mb-14 md:mb-20 pb-8 border-b border-stone-200">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-2 h-2 rounded-full bg-stone-900 inline-block" />
                  <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono">
                    {lang === "da" ? "01 / Faglig Ekspertise & Ydelser" : "01 / Expertise & Services"}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-stone-900 uppercase leading-[1.1]">
                  {lang === "da" ? (
                    <>
                      Totalentreprise & Tømrerarbejde{" "}
                      <span className="font-serif italic font-normal lowercase text-stone-500">i praksis</span>
                    </>
                  ) : (
                    <>
                      Turnkey & Carpentry{" "}
                      <span className="font-serif italic font-normal lowercase text-stone-500">in practice</span>
                    </>
                  )}
                </h2>
              </div>
              <div className="flex items-center gap-4 lg:self-end pb-1">
                <div className="w-10 h-[1px] bg-stone-300 hidden sm:block" />
                <span className="text-xs uppercase tracking-[0.25em] text-stone-400 font-mono">
                  CPH Collective · København & Sjælland
                </span>
              </div>
            </div>
          </div>

          {/* 2-Column Architectural Editorial Grid */}
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
              {/* Column 1: Totalentreprise & Projektering */}
              <FadeIn direction="up" delay={150}>
                <div className="relative space-y-12">
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <span className="text-xs uppercase tracking-[0.3em] text-stone-400 font-mono block">
                        {lang === "da" ? "Totalentreprise" : "Turnkey Solutions"}
                      </span>
                      <div className="w-10 h-10 rounded-full border border-stone-200 bg-stone-50 flex items-center justify-center text-stone-400 group-hover:text-stone-900 transition-colors shrink-0" aria-hidden="true">
                        <Building2 className="w-4 h-4 stroke-[1.5]" />
                      </div>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-light tracking-tight text-stone-900 uppercase leading-snug mb-6 pr-4">
                      {lang === "da"
                        ? "Hvad får I ud af én ansvarlig totalentreprise?"
                        : "What do you gain from a single accountable turnkey partner?"}
                    </h2>
                    <div className="space-y-5 text-stone-600 text-base md:text-lg font-light leading-relaxed">
                      <p>
                        {lang === "da" ? (
                          <>
                            Ved en{" "}
                            <Link
                              to="/totalentreprise"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              totalentreprise
                            </Link>{" "}
                            samles projektering, planlægning, faggrupper og udførelse hos én ansvarlig partner. Det giver jer færre grænseflader og et klarere overblik over beslutninger, tidsplan og økonomi. Løsningen passer især til byggeri med mange aktører og høje krav til koordinering.
                          </>
                        ) : (
                          <>
                            With a{" "}
                            <Link
                              to="/en/services/turnkey"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              turnkey contract
                            </Link>
                            , engineering, planning, trades, and execution are united under one accountable partner. This provides fewer interfaces and clearer oversight of decisions, schedule, and budget. The solution is ideal for projects with many stakeholders and high coordination requirements.
                          </>
                        )}
                      </p>
                      <p>
                        {lang === "da"
                          ? "Vi begynder med at afklare behov, omfang og forventninger. Derefter kan vi håndtere projektering af byggeri, koordinere underentreprenører og følge op på kvalitet og fremdrift. Vores projektarbejde er gennemskueligt, og I får løbende kommunikation, så vigtige valg bliver truffet på et oplyst grundlag."
                          : "We start by clarifying needs, scope, and expectations. We then manage building engineering, coordinate subcontractors, and supervise quality and progress with full transparency, ensuring informed decisions throughout."}
                      </p>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-stone-200">
                    <h3 className="text-lg md:text-xl font-normal tracking-wide text-stone-900 uppercase mb-4">
                      {lang === "da"
                        ? "Fra projektering af byggeri til præcis udførelse"
                        : "From building design to precise execution"}
                    </h3>
                    <p className="text-stone-600 text-base md:text-lg font-light leading-relaxed">
                      {lang === "da" ? (
                        <>
                          God{" "}
                          <Link
                            to="/projektering-af-byggeri"
                            className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                          >
                            projektering af byggeri
                          </Link>{" "}
                          skaber et brugbart grundlag for udførelsen. Vi binder tegninger, materialer, håndværk og pladsforhold sammen, så løsningerne fungerer i praksis. Når projektet går i gang, følger vi aftalerne tæt og tilpasser koordineringen til byggepladsens faktiske forhold.
                        </>
                      ) : (
                        <>
                          Thorough{" "}
                          <Link
                            to="/en/services/building-engineering"
                            className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                          >
                            building design & engineering
                          </Link>{" "}
                          creates a solid foundation for execution. We bind drawings, materials, craftsmanship, and site conditions together so solutions work in practice.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </FadeIn>

              {/* Column 2: Tømrerarbejde, Renovering & Byggepladsservice */}
              <FadeIn direction="up" delay={250}>
                <div className="relative space-y-12 lg:border-l lg:border-stone-200 lg:pl-16">
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <span className="text-xs uppercase tracking-[0.3em] text-stone-400 font-mono block">
                        {lang === "da" ? "Tømrer & Renovering" : "Carpentry & Renovation"}
                      </span>
                      <div className="w-10 h-10 rounded-full border border-stone-200 bg-stone-50 flex items-center justify-center text-stone-400 group-hover:text-stone-900 transition-colors shrink-0" aria-hidden="true">
                        <Hammer className="w-4 h-4 stroke-[1.5]" />
                      </div>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-light tracking-tight text-stone-900 uppercase leading-snug mb-6 pr-4">
                      {lang === "da"
                        ? "Tømrerarbejde og renovering, hvor detaljen tæller"
                        : "Carpentry and renovation where details matter"}
                    </h2>
                    <div className="space-y-5 text-stone-600 text-base md:text-lg font-light leading-relaxed">
                      <p>
                        {lang === "da" ? (
                          <>
                            Som udførende tømrervirksomhed leverer vi professionelt{" "}
                            <Link
                              to="/toemrerarbejde"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              tømrerarbejde
                            </Link>{" "}
                            fra konstruktive træløsninger og lofter til specialinventar, akustikpaneler og præcis montage. Den praktiske faglighed gør det lettere at omsætte projektets krav til holdbare løsninger med en ensartet finish.
                          </>
                        ) : (
                          <>
                            As an experienced carpentry contractor, we deliver professional{" "}
                            <Link
                              to="/en/services/carpentry"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              carpentry work
                            </Link>{" "}
                            from structural timber frameworks and ceilings to custom joinery, acoustic panels, and precision installation.
                          </>
                        )}
                      </p>
                      <p>
                        {lang === "da" ? (
                          <>
                            Vi udfører{" "}
                            <Link
                              to="/renovering"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              renovering
                            </Link>
                            , ombygning og{" "}
                            <Link
                              to="/totalrenovering"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              totalrenovering
                            </Link>{" "}
                            af eksisterende bygninger samt tømrerleverancer til nybyggeri. Det kan være kontorer, erhvervslokaler, historiske trækonstruktioner eller særligt inventar, hvor den nye løsning skal fungere med bygningens udtryk og daglige brug.
                          </>
                        ) : (
                          <>
                            We carry out{" "}
                            <Link
                              to="/en/services/renovation"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              renovation
                            </Link>
                            , remodeling, and{" "}
                            <Link
                              to="/en/services/total-renovation"
                              className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                            >
                              total renovation
                            </Link>{" "}
                            of existing commercial spaces as well as carpentry deliveries for new builds.
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-stone-200">
                    <h3 className="text-lg md:text-xl font-normal tracking-wide text-stone-900 uppercase mb-4">
                      {lang === "da"
                        ? "Hvordan holder vi en aktiv byggeplads i gang?"
                        : "How do we keep an active construction site running?"}
                    </h3>
                    <p className="text-stone-600 text-base md:text-lg font-light leading-relaxed">
                      {lang === "da" ? (
                        <>
                          Vores{" "}
                          <Link
                            to="/byggepladsservice"
                            className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                          >
                            byggepladsservice
                          </Link>{" "}
                          understøtter den daglige drift på aktive byggepladser. Vi kan indgå som håndværker og pladsservicepartner i den eksisterende pladsorganisation og hjælpe med adgangsforhold, logistik, pladssikkerhed og koordinering. Målet er et effektivt arbejdsflow på pladsen.
                        </>
                      ) : (
                        <>
                          Our{" "}
                          <Link
                            to="/en/services/construction-site-services"
                            className="text-stone-900 font-normal underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 hover:text-stone-950 transition-colors"
                          >
                            construction site services
                          </Link>{" "}
                          support daily operations on active job sites, assisting with logistics, site safety, access control, and trades coordination.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* All 9 Services Grid - Complete Direct Linking */}
          <div className="mt-16 md:mt-24 pt-12 border-t border-stone-200">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
              <div>
                <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-2">
                  {lang === "da" ? "Komplet Ydelsesoversigt" : "Complete Service Directory"}
                </span>
                <h3 className="text-2xl md:text-3xl font-light uppercase tracking-tight text-stone-900">
                  {lang === "da" ? "Udforsk alle vores faglige ydelser" : "Explore all our specialized services"}
                </h3>
              </div>
              <Link
                to={servicesPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start md:self-end"
              >
                <span>{lang === "da" ? "Se ydelser i dybden" : "View services overview"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES_DATA.map((srv, idx) => {
                const sSlug = srv.slug?.[lang] || srv.id;
                const sPath = lang === "da" ? `/ydelser/${sSlug}` : `/en/services/${sSlug}`;
                const sTitle = srv.title[lang] || srv.title.da;
                const sSub = srv.subtitle?.[lang] || srv.subtitle?.da;

                return (
                  <Link
                    key={srv.id}
                    to={sPath}
                    className="group p-6 md:p-8 bg-white border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono text-stone-400 group-hover:text-stone-900 transition-colors">
                          0{idx + 1}
                        </span>
                        <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-900 group-hover:translate-x-1 transition-all" />
                      </div>
                      <h4 className="text-lg font-light uppercase tracking-wide text-stone-900 mb-2 group-hover:text-stone-700 transition-colors">
                        {sTitle}
                      </h4>
                      <p className="text-xs sm:text-sm text-stone-600 font-light leading-relaxed line-clamp-3">
                        {sSub}
                      </p>
                    </div>
                    <span className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 group-hover:text-stone-900 flex items-center gap-1.5 pt-4 border-t border-stone-100">
                      <span>{lang === "da" ? "Læs om ydelsen" : "Read more"}</span>
                      <span>→</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Section Footer: Dækning & Kontakt (Architectural Editorial Style) */}
          <FadeIn direction="up" delay={300}>
            <div className="mt-16 md:mt-24 p-8 md:p-12 lg:p-14 bg-stone-900 text-stone-100 relative overflow-hidden flex flex-col lg:flex-row lg:items-center justify-between gap-8 md:gap-12 shadow-sm">
              {/* Subtle background detail */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-stone-800/40 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
              
              <div className="max-w-3xl relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span className="text-xs uppercase tracking-[0.3em] text-stone-400 font-mono">
                    {lang === "da" ? "Dækning & Samarbejde" : "Coverage & Consultation"}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white uppercase mb-4 leading-snug">
                  {lang === "da"
                    ? "Klar til at få jeres byggeprojekt sikkert videre?"
                    : "Ready to move your building project safely forward?"}
                </h2>
                <p className="text-stone-300 text-base md:text-lg font-light leading-relaxed">
                  {lang === "da" ? (
                    <>
                      CPH Collective har base i København og løser opgaver på hele Sjælland og i Nordsjælland. Vi kører også ud til byggeprojekter i resten af Danmark, når opgaven kræver vores kompetencer.{" "}
                      <Link
                        to={contactPath}
                        className="text-white font-normal underline underline-offset-4 decoration-stone-500 hover:decoration-white transition-colors"
                      >
                        Kontakt os
                      </Link>{" "}
                      for en uforpligtende gennemgang og et tilbud baseret på projektets behov og omfang.
                    </>
                  ) : (
                    <>
                      CPH Collective is based in Copenhagen and delivers projects across Zealand and North Zealand, with nationwide coverage when specialized competencies are required.{" "}
                      <Link
                        to={contactPath}
                        className="text-white font-normal underline underline-offset-4 decoration-stone-500 hover:decoration-white transition-colors"
                      >
                        Contact us
                      </Link>{" "}
                      for a non-binding review and quote tailored to your project.
                    </>
                  )}
                </p>
              </div>

              <div className="shrink-0 relative z-10">
                <Link
                  to={contactPath}
                  className="inline-flex items-center gap-3 bg-white text-stone-900 hover:bg-stone-100 hover:shadow-lg transition-all duration-300 px-8 py-5 text-xs uppercase tracking-[0.25em] font-medium font-mono group"
                >
                  <span>{lang === "da" ? "Kontakt os her" : "Contact Us"}</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Horizontal Visual Content Block - Turnkey Focus */}
      <section className="relative min-h-[80vh] flex items-center border-b border-stone-200 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Parallax speed={-0.1} type="background" className="w-full h-full">
            <img
              src={getOptimizedImage("https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-erhvervsbyggeri-kontor-facade.jpg", 1600)}
              alt={lang === "da" ? "CPH Collective - Nordhavn Pavillonen, moderne erhvervsbyggeri med træfacade" : "CPH Collective - Modern commercial construction with wood facade"}
              className="w-full h-full object-cover object-center"
            />
          </Parallax>
          <div className="absolute inset-0 bg-stone-50/15" />
        </div>

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 py-20 flex justify-start">
          <Parallax speed={0.03} type="text" className="max-w-2xl">
            <div className="bg-stone-50/90 backdrop-blur-md p-8 md:p-12 border border-stone-200/60 shadow-lg">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                  {lang === "da" ? "02 / Totalentreprise" : "02 / Turnkey Solutions"}
                </span>
                <h2 className="text-2xl md:text-4xl font-light tracking-tight text-stone-900 uppercase mb-6">
                  {lang === "da" ? "Ét enkelt kontaktpunkt" : "Single Point of Accountability"}
                </h2>
                <p className="text-stone-700 text-base font-light leading-relaxed mb-8">
                  {lang === "da"
                    ? "Med totalentreprise overtager vi det fulde ansvar for hele byggeprojektet – fra planlægning til færdiggørelse. Vi fungerer som dit eneste kontaktpunkt og koordinerer alle involverede faggrupper, hvilket giver dig fuld tryghed."
                    : "With turnkey services, we take full responsibility for the entire construction project—from planning to completion. We act as a single point of contact and coordinate all involved trades, giving you complete peace of mind."}
                </p>
                <Link
                  to={servicesPath}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 border-b border-stone-900 pb-2 hover:text-stone-600 hover:border-stone-600 transition-colors font-mono"
                >
                  {lang === "da" ? "Udforsk totalentreprise" : "Explore Turnkey Services"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </FadeIn>
            </div>
          </Parallax>
        </div>
      </section>

      {/* Featured Projects Section */}
      {loading ? (
        <section className="py-24 md:py-36 bg-stone-50 border-b border-stone-200">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="animate-pulse flex flex-col gap-8">
              <div className="h-4 bg-stone-200 w-24" />
              <div className="h-10 bg-stone-200 w-64 mb-8" />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-stone-100 h-[500px] border border-stone-200" />
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Featured Projects - Editorial Sticky Horizontal Scroll (Desktop) */}
          <section 
            ref={sectionRef} 
            className="hidden lg:block relative w-full h-[250vh] bg-stone-50 border-b border-stone-200"
          >
            <div className="sticky top-0 h-screen flex items-center overflow-hidden">
              <div className="w-full h-full flex">
                {/* Left Column: Sticky Featured Project (38% width) */}
                <div className="w-[38%] h-full flex flex-col justify-between p-12 xl:p-16 border-r border-stone-200/60 bg-stone-100/30 z-10 shrink-0">
                  <div>
                    <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                      {lang === "da" ? "03 / Referencer" : "03 / References"}
                    </span>
                    <h2 className="text-3xl xl:text-4xl font-light tracking-tight text-stone-900 uppercase leading-tight">
                      {t("home.featured.title")}
                    </h2>
                  </div>

                  {featuredProject && (
                    <div className="my-auto py-8">
                      <Link
                        to={lang === "da" ? `/projekter/${featuredProject.project_slug?.[lang] || featuredProject.project_slug}` : `/en/projects/${featuredProject.project_slug?.[lang] || featuredProject.project_slug}`}
                        className="group block"
                      >
                        <div className="overflow-hidden aspect-[1.58] bg-stone-200 relative mb-6 border border-stone-200/40">
                          <img
                            src={getOptimizedImage(featuredProject.images?.[0], 1200)}
                            alt={`${featuredProject.title?.[lang] || featuredProject.title} – ${featuredProject.project_type?.[lang] || featuredProject.project_type} | CPH Collective`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute top-4 left-4 bg-stone-900/80 backdrop-blur-sm text-white text-[9px] uppercase tracking-[0.2em] font-mono px-3 py-1.5">
                            {lang === "da" ? "Seneste reference" : "Latest Reference"}
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-mono block mb-2">
                          {featuredProject.project_type?.[lang] || featuredProject.project_type}
                        </span>
                        <h3 className="text-xl xl:text-2xl font-light tracking-tight text-stone-900 uppercase group-hover:text-stone-600 transition-colors leading-tight">
                          {featuredProject.title?.[lang] || featuredProject.title}
                        </h3>
                        <p className="text-xs text-stone-500 font-mono mt-2">
                          {featuredProject.location?.[lang] || featuredProject.location} · {featuredProject.role?.[lang] || featuredProject.role}
                        </p>
                      </Link>
                    </div>
                  )}

                  <div>
                    <Link
                      to={projectsPath}
                      className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 border-b border-stone-900 pb-2 hover:text-stone-600 hover:border-stone-600 transition-colors font-mono"
                    >
                      {t("home.featured.viewAll")}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Right Column: Horizontal Scrolling Track (62% width) */}
                <div className="w-[62%] h-full flex items-center relative overflow-hidden bg-stone-50/50">
                  {/* Vertical grid line divider for editorial feel */}
                  <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-stone-200/40 pointer-events-none" />
                  
                  <div 
                    ref={trackRef}
                    className="flex gap-10 px-16 xl:px-24 transition-transform duration-100 ease-out"
                    style={{
                      transform: `translateX(-${translateX}px)`
                    }}
                  >
                    {scrollingProjects.map((project) => {
                      const pTitle = typeof project.title === "object" ? project.title[lang] : project.title;
                      const pType = typeof project.project_type === "object" ? project.project_type[lang] : project.project_type;
                      const pLoc = typeof project.location === "object" ? project.location[lang] : project.location;
                      const pRole = typeof project.role === "object" ? project.role[lang] : project.role;
                      const pSlug = typeof project.project_slug === "object" ? project.project_slug[lang] : project.project_slug;

                      return (
                        <div key={project.id} className="w-[380px] xl:w-[440px] shrink-0">
                          <Link
                            to={lang === "da" ? `/projekter/${pSlug}` : `/en/projects/${pSlug}`}
                            className="group block"
                          >
                            <div className="overflow-hidden aspect-[1.58] bg-stone-200 relative mb-5 border border-stone-200/40">
                              <img
                                src={getOptimizedImage(project.images?.[0], 800)}
                                alt={`${pTitle} – ${pType} | CPH Collective`}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              />
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-mono block mb-2">
                              {pType}
                            </span>
                            <h4 className="text-lg xl:text-xl font-light tracking-tight text-stone-900 uppercase group-hover:text-stone-600 transition-colors leading-snug line-clamp-2">
                              {pTitle}
                            </h4>
                            <p className="text-xs text-stone-400 font-mono mt-2 mb-4">
                              {pLoc} · {pRole}
                            </p>
                            
                            <div className="inline-flex items-center gap-3">
                              <span className="text-[11px] font-bold tracking-widest text-stone-500 uppercase group-hover:text-stone-900 transition-colors duration-300">
                                {lang === "da" ? "Læs mere" : "Read More"}
                              </span>
                              <svg className="w-6 h-3 text-stone-900 transform group-hover:translate-x-1.5 transition-transform duration-300 ease-out" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"></path>
                              </svg>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Projects - Responsive Swipe Layout (Mobile & Tablet) */}
          <section className="block lg:hidden py-20 bg-stone-50 border-b border-stone-200">
            <div className="w-full px-6 md:px-12">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                  <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-3">
                    {lang === "da" ? "03 / Referencer" : "03 / References"}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900 uppercase">
                    {t("home.featured.title")}
                  </h2>
                </div>
              </div>

              {/* Large Featured Project */}
              {featuredProject && (
                <div className="mb-16">
                  <Link
                    to={lang === "da" ? `/projekter/${featuredProject.project_slug?.[lang] || featuredProject.project_slug}` : `/en/projects/${featuredProject.project_slug?.[lang] || featuredProject.project_slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden aspect-[16/10] bg-stone-200 relative mb-6 border border-stone-200/40">
                      <img
                        src={getOptimizedImage(featuredProject.images?.[0], 1000)}
                        alt={`${featuredProject.title?.[lang] || featuredProject.title} – ${featuredProject.project_type?.[lang] || featuredProject.project_type} | CPH Collective`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-stone-900/80 backdrop-blur-sm text-white text-[9px] uppercase tracking-[0.2em] font-mono px-3 py-1.5">
                        {lang === "da" ? "Seneste reference" : "Latest Reference"}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-mono block mb-2">
                      {featuredProject.project_type?.[lang] || featuredProject.project_type}
                    </span>
                    <h3 className="text-2xl font-light tracking-tight text-stone-900 uppercase group-hover:text-stone-600 transition-colors leading-tight">
                      {featuredProject.title?.[lang] || featuredProject.title}
                    </h3>
                    <p className="text-xs text-stone-500 font-mono mt-2">
                      {featuredProject.location?.[lang] || featuredProject.location} · {featuredProject.role?.[lang] || featuredProject.role}
                    </p>
                  </Link>
                </div>
              )}

              {/* Horizontal Swipe Container for the other 6 references */}
              <div className="mb-12">
                <h3 className="text-xs uppercase tracking-[0.25em] text-stone-400 font-mono mb-6">
                  {lang === "da" ? "Flere udvalgte referencer" : "More Featured References"}
                </h3>
                
                <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-none">
                  {scrollingProjects.map((project) => {
                    const pTitle = typeof project.title === "object" ? project.title[lang] : project.title;
                    const pType = typeof project.project_type === "object" ? project.project_type[lang] : project.project_type;
                    const pLoc = typeof project.location === "object" ? project.location[lang] : project.location;
                    const pRole = typeof project.role === "object" ? project.role[lang] : project.role;
                    const pSlug = typeof project.project_slug === "object" ? project.project_slug[lang] : project.project_slug;

                    return (
                      <div key={project.id} className="w-[280px] sm:w-[320px] shrink-0 snap-start">
                        <Link
                          to={lang === "da" ? `/projekter/${pSlug}` : `/en/projects/${pSlug}`}
                          className="group block"
                        >
                          <div className="overflow-hidden aspect-[1.58] bg-stone-200 relative mb-4 border border-stone-200/40">
                            <img
                              src={getOptimizedImage(project.images?.[0], 600)}
                              alt={`${pTitle} – ${pType} | CPH Collective`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-[9px] uppercase tracking-[0.25em] text-stone-500 font-mono block mb-1.5">
                            {pType}
                          </span>
                          <h4 className="text-base font-light tracking-tight text-stone-900 uppercase group-hover:text-stone-600 transition-colors leading-snug line-clamp-2">
                            {pTitle}
                          </h4>
                          <p className="text-[11px] text-stone-400 font-mono mt-1 mb-3">
                            {pLoc}
                          </p>
                          
                          <div className="inline-flex items-center gap-2">
                            <span className="text-[10px] font-bold tracking-widest text-stone-500 uppercase">
                              {lang === "da" ? "Læs mere" : "Read More"}
                            </span>
                            <svg className="w-4 h-2.5 text-stone-900" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"></path>
                            </svg>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* View All Projects Button */}
              <div className="text-center">
                <Link
                  to={projectsPath}
                  className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-stone-900 text-white hover:bg-stone-800 transition-all duration-500 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold font-mono"
                >
                  {t("home.featured.viewAll")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Horizontal Visual Content Block - Carpentry Focus */}
      <section className="relative min-h-[80vh] flex items-center border-b border-stone-200 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Parallax speed={-0.1} type="background" className="w-full h-full">
            <img
              src={getOptimizedImage("https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerarbejde-specialdesignet-traeinterioer.jpg", 1600)}
              alt={lang === "da" ? "CPH Collective - Detaljeret snedker- og tømrerarbejde i massivt træ" : "CPH Collective - High-end bespoke carpentry and joinery in solid wood"}
              className="w-full h-full object-cover object-center"
            />
          </Parallax>
          <div className="absolute inset-0 bg-stone-50/15" />
        </div>

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 py-20 flex justify-end">
          <Parallax speed={0.03} type="text" className="max-w-2xl">
            <div className="max-w-2xl bg-stone-50/90 backdrop-blur-md p-8 md:p-12 border border-stone-200/60 shadow-lg text-right">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                  {lang === "da" ? "04 / Tømrerarbejde & Specialprojekter" : "04 / Carpentry & Custom Builds"}
                </span>
                <h2 className="text-2xl md:text-4xl font-light tracking-tight text-stone-900 uppercase mb-6">
                  {lang === "da" ? "Præcisionshåndværk" : "Precision Craftsmanship"}
                </h2>
                <p className="text-stone-700 text-base font-light leading-relaxed mb-8">
                  {lang === "da"
                    ? "Vi udfører professionelt tømrerarbejde til både nybyggeri og renoveringsprojekter. Vores arbejde er kendetegnet ved høj håndværksmæssig kvalitet, præcision og respekt for andre aktører på pladsen."
                    : "We perform professional carpentry work for both new construction and renovation projects. Our work is defined by high craftsmanship, precision, and respect for other site actors."}
                </p>
                <div className="flex flex-wrap items-center justify-end gap-4">
                  <Link
                    to={lang === "da" ? "/specialprojekter" : "/en/services/custom-projects"}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-600 hover:text-stone-900 transition-colors font-mono"
                  >
                    <span>{lang === "da" ? "Specialprojekter" : "Custom Projects"}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to={lang === "da" ? "/toemrerarbejde" : "/en/services/carpentry"}
                    className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 border-b border-stone-900 pb-2 hover:text-stone-600 hover:border-stone-600 transition-colors font-mono"
                  >
                    <span>{lang === "da" ? "Udforsk tømrerarbejde" : "Explore Carpentry Services"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </FadeIn>
            </div>
          </Parallax>
        </div>
      </section>



      {/* Why Choose Us / Corporate Stats */}
      <section className="py-24 md:py-36 bg-stone-50">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <FadeIn direction="up">
            <div className="max-w-3xl mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono block mb-4">
                {t("home.why.number")}
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight text-stone-900 uppercase">
                {lang === "da" ? "Bygget til ansvarlighed" : "Engineered for Accountability"}
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Layers className="w-6 h-6 text-stone-600" />,
                title: lang === "da" ? "Ét dedikeret kontaktpunkt" : "One Dedicated Contact",
                desc: lang === "da"
                  ? "En enkelt ansvarlig projektleder gennem hele projektets livscyklus."
                  : "A single accountable project manager throughout the entire project lifecycle."
              },
              {
                icon: <Sparkles className="w-6 h-6 text-stone-600" />,
                title: lang === "da" ? "Komplekse opgaver" : "Complex Assignments",
                desc: lang === "da"
                  ? "Dokumenteret erfaring med totalentrepriser og komplekse byggeopgaver."
                  : "Proven experience with turnkey projects and complex construction assignments."
              },
              {
                icon: <ShieldCheck className="w-6 h-6 text-stone-600" />,
                title: lang === "da" ? "Professionelt samarbejde" : "Professional Site Collaboration",
                desc: lang === "da"
                  ? "En samarbejdsorienteret, professionel tilgang til at arbejde sammen med andre faggrupper på aktive pladser."
                  : "A collaborative, professional approach to working alongside other trades on active sites."
              },
              {
                icon: <Clock className="w-6 h-6 text-stone-600" />,
                title: lang === "da" ? "Struktureret udførelse" : "Structured Execution",
                desc: lang === "da"
                  ? "Høj kvalitet, ingeniørmæssig udførelse med stram styring af tidsplan og budget."
                  : "High-quality, engineered execution with rigorous scheduling and budget management."
              }
            ].map((item, idx) => (
              <FadeIn key={idx} direction="up" delay={idx * 100} className="h-full">
                <div className="border border-stone-200 bg-white p-8 hover:border-stone-300 hover:shadow-sm transition-all duration-300 h-full">
                  <div className="mb-6">{item.icon}</div>
                  <h3 className="text-lg font-light uppercase tracking-wider text-stone-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 text-sm font-light leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="bg-stone-100 py-20 md:py-32 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <FadeIn direction="up">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 uppercase mb-8">
                {lang === "da" ? "Klar til at drøfte dit projekt?" : "Ready to Discuss Your Project?"}
              </h2>
              <p className="text-stone-600 text-lg font-light mb-12 max-w-2xl mx-auto leading-relaxed">
                {lang === "da"
                  ? "Kontakt os i dag for en uforpligtende konsultation eller for at anmode om et detaljeret projekttilbud. Lad os bygge noget enestående sammen."
                  : "Contact us today for a non-binding consultation or to request a detailed project quote. Let's build something exceptional together."}
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
                <Link
                  to={contactPath}
                  className="bg-stone-900 text-white hover:bg-stone-800 transition-all duration-500 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold font-mono w-full sm:w-auto"
                >
                  {t("cta.requestQuote")}
                </Link>
                <Link
                  to={contactPath}
                  className="border border-stone-300 text-stone-900 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all duration-500 px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold font-mono w-full sm:w-auto"
                >
                  {lang === "da" ? "Kontakt os" : "Contact Us"}
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
